import type { UseQueryResult } from '@tanstack/react-query';
import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { keysToCamel } from '@lib/case_converter';
import { PARTNER_ORGANIZATION_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * Raw wire shape returned by GET /api/v1/partner/organization (snake_case).
 * organization_id is intentionally omitted by the backend — the caller
 * already has it in their own JWT (org_id claim).
 */
interface PartnerOwnOrganizationResponse {
  organization_name: string | null;
  /** ISO 8601 timestamp; null for legacy orgs created before this field existed. */
  organization_created_at: string | null;
}

export interface PartnerOwnOrganization {
  organizationName: string | null;
  /** ISO 8601 timestamp; null for legacy orgs created before this field existed. */
  organizationCreatedAt: string | null;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches the organization scoped to the caller's own JWT (org_id claim).
 *
 * Partner-role only (PartnerAdmin/PartnerViewer) — AceUp staff pick an org
 * from the dropdown instead, so this should stay disabled for them.
 *
 * @param enabled  Pass `false` to suppress the call (e.g. for AceUp staff roles).
 */

export const usePartnerOrganizationQuery = (
  enabled: boolean
): UseQueryResult<PartnerOwnOrganization, Error> =>
  useBasicQuery<PartnerOwnOrganizationResponse, PartnerOwnOrganization>(
    PARTNER_ORGANIZATION_PATH,
    undefined,
    {
      enabled,
      // keysToCamel deep-converts every key (snake_case -> camelCase); it's
      // untyped at the boundary, hence the cast back to PartnerOwnOrganization.
      select: (raw) => keysToCamel(raw) as PartnerOwnOrganization,
    }
  );
